import { formatTime } from "../../utils/formatTime";
import React, { useCallback, useRef, useState } from "react";

interface ProgressBarProps {
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
}

export function ProgressBar({ currentTime, duration, onSeek }: ProgressBarProps) {
  const progress = duration > 0 && Number.isFinite(duration) ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleSeek = useCallback(
    (clientX: number) => {
      if (!progressBarRef.current) return;
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      onSeek(pos * duration);
    },
    [duration, onSeek]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true);
      handleSeek(e.clientX);
    },
    [handleSeek]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        handleSeek(e.clientX);
      }
    },
    [isDragging, handleSeek]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className="flex items-center gap-2 select-none">
      <span className="text-white text-sm min-w-[40px]">{formatTime(currentTime)}</span>
      <div
        ref={progressBarRef}
        className={`flex-1 h-2 bg-white/30 rounded-full relative group ${isDragging ? "cursor-grabbing" : "cursor-pointer"}`}
        onMouseDown={handleMouseDown}>
        <div
          className={`absolute left-0 top-0 h-full bg-purple-500 rounded-full pointer-events-none ${
            isDragging ? "" : "transition-[width] duration-150 ease-out"
          }`}
          style={{ width: `${progress}%` }}>
          <div
            className={`absolute h-4 w-4 bg-purple-500 rounded-full top-1/2 -translate-y-1/2 right-0 translate-x-1/2 pointer-events-none ${
              isDragging ? "opacity-100" : "opacity-0 group-hover:opacity-100 transition-opacity"
            }`}
          />
        </div>
      </div>
      <span className="text-white text-sm min-w-[40px]">{formatTime(duration)}</span>
    </div>
  );
}
